import { useCallback, useMemo, useState } from 'react';
import { type CommandContext, type CommandResult, getCommandHandler } from '../grid/commands';
import { getCommandId } from '../grid/commands/utils';
import type { Command } from '../menu/types';
import type { RelationPickerTarget } from '../grid/components/dialogs/RelationPickerDialog';
import type { AutoInvoiceNavigationModal } from '../grid/commands/types';
import type { InvoiceDeliveryPreview } from '../grid/api/invoiceDeliveryApi';
import type { SimpleOutboundPreview } from '../grid/api/simpleOutboundApi';
import { normalizeGridKey } from '../grid/utils';
import type { NavigationRequestOptions } from '../grid/types/navigation';
import { extractErrorMessage } from '../../utils/errorUtils';

type UseDetailFormRelationsOptions = {
  commands?: Command[];
  parentId?: string;
  entityId?: string;
  entityData?: Record<string, unknown>;
  hasUnsavedChanges?: boolean;
  navigate: (path: string | number, options?: NavigationRequestOptions) => void;
  currentPath: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  onNavigate: (url: string) => void;
  prepareInvoiceDeliveryPreview?: (
    invoiceId: number,
    templateId?: string
  ) => Promise<InvoiceDeliveryPreview>;
  prepareSimpleOutboundPreview?: (clientId: number) => Promise<SimpleOutboundPreview>;
  prepareSimpleOutboundReplyPreview?: (args: {
    historyId: string;
    replyAll?: boolean;
  }) => Promise<SimpleOutboundPreview>;
};

export type SendInvoiceToClientModal = {
  invoiceId: number;
  templateId?: string;
  preview: InvoiceDeliveryPreview;
};

export type SendSimpleOutboundModal = {
  clientId: number;
  preview: SimpleOutboundPreview;
};

export const useDetailFormRelations = ({
  commands = [],
  parentId,
  entityId,
  entityData,
  hasUnsavedChanges = false,
  navigate,
  currentPath,
  onError,
  onSuccess,
  onNavigate,
  prepareInvoiceDeliveryPreview,
  prepareSimpleOutboundPreview,
  prepareSimpleOutboundReplyPreview,
}: UseDetailFormRelationsOptions) => {
  const [relationPickerCommand, setRelationPickerCommand] = useState<Command | null>(null);
  const [salaryCalculateCommand, setSalaryCalculateCommand] = useState<Command | null>(null);
  const [aiImplementationTaskId, setAiImplementationTaskId] = useState<number | null>(null);
  const [isAiProviderDialogOpen, setIsAiProviderDialogOpen] = useState(false);
  const [autoInvoiceModal, setAutoInvoiceModal] = useState<AutoInvoiceNavigationModal | null>(null);
  const [sendInvoiceModal, setSendInvoiceModal] = useState<SendInvoiceToClientModal | null>(null);
  const [sendSimpleOutboundModal, setSendSimpleOutboundModal] =
    useState<SendSimpleOutboundModal | null>(null);

  const [relationTargets, setRelationTargets] = useState<Record<string, RelationPickerTarget>>({});
  const selectedRows = useMemo(() => {
    const selectedIds = new Set<string>();
    Object.values(relationTargets).forEach((target) => {
      target.selectedRows?.forEach((id) => selectedIds.add(id));
    });
    return selectedIds.size > 0 ? selectedIds : undefined;
  }, [relationTargets]);
  const hasUnsavedChildChanges = useMemo(
    () => Object.values(relationTargets).some((target) => target.hasUnsavedChanges),
    [relationTargets]
  );

  const registerRelationTarget = useCallback((target: RelationPickerTarget) => {
    const normalizedGridKey = normalizeGridKey(target.gridKey);
    setRelationTargets((current) => ({ ...current, [normalizedGridKey]: target }));

    return () => {
      setRelationTargets((current) => {
        if (current[normalizedGridKey] !== target) return current;
        const next = { ...current };
        delete next[normalizedGridKey];
        return next;
      });
    };
  }, []);

  const openSendInvoiceToClientDialog = useCallback(
    async (invoiceId: number, templateId?: string) => {
      if (!prepareInvoiceDeliveryPreview) {
        return {
          success: false,
          error: 'Send invoice dialog is not available.',
        };
      }

      try {
        const preview = await prepareInvoiceDeliveryPreview(invoiceId, templateId);
        setSendInvoiceModal({ invoiceId, templateId, preview });
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: extractErrorMessage(error, 'Failed to prepare invoice email preview.'),
        };
      }
    },
    [prepareInvoiceDeliveryPreview]
  );

  const openSendSimpleOutboundDialog = useCallback(
    async (clientId: number) => {
      if (!prepareSimpleOutboundPreview) {
        return {
          success: false,
          error: 'Send email dialog is not available.',
        };
      }

      try {
        const preview = await prepareSimpleOutboundPreview(clientId);
        setSendSimpleOutboundModal({ clientId, preview });
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: extractErrorMessage(error, 'Failed to prepare email preview.'),
        };
      }
    },
    [prepareSimpleOutboundPreview]
  );

  const openReplyToFinanceEmailDialog = useCallback(
    async (historyId: string, options?: { replyAll?: boolean }) => {
      if (!prepareSimpleOutboundReplyPreview) {
        return {
          success: false,
          error: 'Reply dialog is not available.',
        };
      }

      try {
        const preview = await prepareSimpleOutboundReplyPreview({
          historyId,
          replyAll: options?.replyAll,
        });
        setSendSimpleOutboundModal({ clientId: preview.clientId, preview });
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: extractErrorMessage(error, 'Failed to prepare reply preview.'),
        };
      }
    },
    [prepareSimpleOutboundReplyPreview]
  );

  const handleDetailCommand = useCallback(
    (command: Command) => {
      const handler = getCommandHandler(getCommandId(command));
      if (!handler) {
        onError(`Command "${command.caption || command.alias}" is not supported.`);
        return;
      }

      const context = {
        parentId: String(parentId ?? entityId ?? ''),
        entityId: String(parentId ?? entityId ?? ''),
        entityData,
        hasUnsavedChanges,
        hasUnsavedChildChanges,
        navigate,
        currentPath,
        selectedRows,
        openRelationPicker: setRelationPickerCommand,
        openSalaryCalculatePicker: setSalaryCalculateCommand,
        openImplementWithAiDialog: setAiImplementationTaskId,
        openConfigureAiProviderDialog: () => setIsAiProviderDialogOpen(true),
        openSendInvoiceToClientDialog,
        openSendSimpleOutboundDialog,
        openReplyToFinanceEmailDialog,
      } as unknown as CommandContext;

      const handleResult = (result: CommandResult) => {
        if (!result.success) {
          onError(result.error || 'Command failed.');
        } else if (result.autoInvoiceModal) {
          setAutoInvoiceModal(result.autoInvoiceModal);
        } else if (result.message) {
          onSuccess(result.message);
        }
      };

      const result = handler(command, context);

      if (result instanceof Promise) {
        result.then(handleResult).catch((error) => {
          onError(extractErrorMessage(error, 'Command failed.'));
        });
      } else {
        handleResult(result);
      }
    },
    [
      entityId,
      entityData,
      hasUnsavedChanges,
      hasUnsavedChildChanges,
      navigate,
      currentPath,
      onError,
      onSuccess,
      parentId,
      selectedRows,
      openSendInvoiceToClientDialog,
      openSendSimpleOutboundDialog,
      openReplyToFinanceEmailDialog,
    ]
  );

  const closeRelationPicker = useCallback(() => setRelationPickerCommand(null), []);
  const closeSalaryCalculatePicker = useCallback(() => setSalaryCalculateCommand(null), []);
  const closeAutoInvoiceModal = useCallback(() => setAutoInvoiceModal(null), []);
  const closeSendInvoiceModal = useCallback(() => setSendInvoiceModal(null), []);
  const closeSendSimpleOutboundModal = useCallback(() => setSendSimpleOutboundModal(null), []);

  return {
    detailCommands: commands.filter((command) => Boolean(getCommandHandler(getCommandId(command)))),
    relationPickerCommand,
    salaryCalculateCommand,
    relationTargets,
    registerRelationTarget,
    handleDetailCommand,
    closeRelationPicker,
    closeSalaryCalculatePicker,
    aiImplementationTaskId,
    closeAiImplementationDialog: () => setAiImplementationTaskId(null),
    isAiProviderDialogOpen,
    closeAiProviderDialog: () => setIsAiProviderDialogOpen(false),
    autoInvoiceModal,
    sendInvoiceModal,
    sendSimpleOutboundModal,
    currentPath,
    onNavigate,
    closeAutoInvoiceModal,
    closeSendInvoiceModal,
    closeSendSimpleOutboundModal,
  };
};
